import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'zhmyak-pwa-hint-dismissed';

/**
 * Подсказка установки PWA. Показывается только в обычном (не standalone)
 * браузере на мобильном:
 *  - iOS Safari — инструкция «Поделиться → На экран Домой»
 *    (системного промпта в iOS нет — это единственный путь установки).
 *  - Android Chrome / Edge — перехватываем beforeinstallprompt и
 *    предлагаем нативный диалог установки своей кнопкой.
 * Скрытие сохраняется в localStorage.
 */
@Component({
  selector: 'app-install-pwa-hint',
  standalone: true,
  imports: [CommonModule],
  template: `
    @if (visible()) {
      <div class="install-hint" role="dialog" aria-live="polite">
        <button class="close-btn" (click)="dismiss()" aria-label="Скрыть подсказку">×</button>

        @if (mode() === 'ios') {
          <div class="hint-row">
            <div class="hint-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 16V4"/>
                <path d="m6 10 6-6 6 6"/>
                <path d="M5 20h14"/>
              </svg>
            </div>
            <div class="hint-text">
              <strong>Установите Жмяк на iPhone</strong>
              <span>
                Нажмите
                <svg class="inline-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-label="Поделиться">
                  <path d="M12 16V4"/>
                  <path d="m8 8 4-4 4 4"/>
                  <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7"/>
                </svg>
                «Поделиться» → «На экран „Домой"».
              </span>
            </div>
          </div>
        } @else if (mode() === 'android') {
          <div class="hint-row">
            <div class="hint-icon" aria-hidden="true">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3v12"/>
                <path d="m17 8-5-5-5 5"/>
                <path d="M5 21h14"/>
              </svg>
            </div>
            <div class="hint-text">
              <strong>Установить приложение</strong>
              <span>Откройте Жмяк с домашнего экрана как обычное приложение.</span>
            </div>
            <button type="button" class="install-btn" (click)="install()">Установить</button>
          </div>
        }
      </div>
    }
  `,
  styles: [`
    :host { display: contents; }

    .install-hint {
      position: fixed;
      left: 12px;
      right: 12px;
      bottom: 12px;
      z-index: 1200;
      padding: 12px 14px;
      background: var(--bg-card, #fff);
      color: var(--text-primary, #111);
      border: 1px solid var(--border-default, #e5e7eb);
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
      display: flex;
      align-items: stretch;
      gap: 10px;
      font-family: var(--font-main, system-ui, sans-serif);
    }

    .close-btn {
      position: absolute;
      top: 6px;
      right: 8px;
      width: 26px; height: 26px;
      border: none; background: transparent;
      color: var(--text-muted, #6b7280);
      font-size: 22px; line-height: 1;
      cursor: pointer; border-radius: 6px;
    }
    .close-btn:hover { background: var(--bg-hover, rgba(0,0,0,.04)); color: var(--text-primary); }

    .hint-row {
      display: flex; align-items: center; gap: 12px;
      width: 100%; padding-right: 24px;
    }

    .hint-icon {
      flex-shrink: 0;
      width: 38px; height: 38px;
      border-radius: 10px;
      background: var(--accent-green, #0cbf0c);
      color: #fff;
      display: flex; align-items: center; justify-content: center;
    }

    .hint-text {
      display: flex; flex-direction: column; gap: 2px;
      flex: 1; min-width: 0;
      font-size: 13px; line-height: 1.4;
    }
    .hint-text strong { font-size: 14px; font-weight: 600; }
    .hint-text span { color: var(--text-secondary, #4b5563); }

    .inline-icon {
      vertical-align: -2px;
      margin: 0 2px;
    }

    .install-btn {
      flex-shrink: 0;
      padding: 8px 14px;
      border: none;
      background: var(--accent-green, #0cbf0c);
      color: #fff;
      font: inherit; font-weight: 600; font-size: 13px;
      border-radius: 8px;
      cursor: pointer;
      transition: filter 0.15s;
    }
    .install-btn:hover { filter: brightness(0.95); }

    /* Десктоп — на ПК у пользователя есть нативная кнопка установки в адресной
       строке, наша подсказка лишняя. */
    @media (min-width: 1025px) {
      .install-hint { display: none; }
    }
  `]
})
export class InstallPwaHintComponent implements OnInit {
  protected visible = signal(false);
  protected mode = signal<'ios' | 'android' | null>(null);

  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  ngOnInit(): void {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return;
    if (this.isInstalled()) return;
    if (this.isDismissed()) return;

    if (this.isIOS()) {
      // На iOS системного beforeinstallprompt нет — показываем инструкцию.
      this.mode.set('ios');
      this.visible.set(true);
      return;
    }

    // Android/Chrome/Edge — ждём системное событие готовности к установке.
    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      this.deferredPrompt = e as BeforeInstallPromptEvent;
      this.mode.set('android');
      this.visible.set(true);
    });

    // После установки скрываем подсказку и помечаем dismissed.
    window.addEventListener('appinstalled', () => {
      this.dismiss();
    });
  }

  protected dismiss(): void {
    try { localStorage.setItem(DISMISSED_KEY, '1'); } catch {}
    this.visible.set(false);
  }

  protected async install(): Promise<void> {
    if (!this.deferredPrompt) return;
    try {
      await this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        this.visible.set(false);
      }
    } catch {
      // ignore
    } finally {
      this.deferredPrompt = null;
    }
  }

  private isInstalled(): boolean {
    const standaloneMatch = window.matchMedia?.('(display-mode: standalone)').matches ?? false;
    const iosStandalone = (navigator as { standalone?: boolean }).standalone === true;
    return standaloneMatch || iosStandalone;
  }

  private isDismissed(): boolean {
    try { return localStorage.getItem(DISMISSED_KEY) === '1'; }
    catch { return false; }
  }

  private isIOS(): boolean {
    const ua = navigator.userAgent || '';
    // iPad на iPadOS 13+ маскируется под Mac — добавляем проверку touch-устройства.
    const isIPadOS = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    return /iPhone|iPad|iPod/.test(ua) || isIPadOS;
  }
}
